// ABOUTME: Helpers to adapt entity-catalog option groups (HCP-108901) into form-ready shapes.
// ABOUTME: UoM refs -> {value,label} dropdown options, backend day codes -> DayIdentifier[], with fallbacks.

import {UnitOfMeasureRef} from '../types'
import {DAYS, DAY_CODE_TO_ID, DayCode, DayIdentifier} from '../types/deliveryWindow'

export interface SelectOption {
  value: string
  label: string
}

/**
 * Converts UoM references into `{value: id, label: displaySymbol|name}` dropdown options.
 * Returns an empty array when the backend group is absent.
 */
export const uomRefsToOptions = (refs?: UnitOfMeasureRef[]): SelectOption[] =>
  (refs ?? []).map((ref) => ({value: ref.id, label: ref.displaySymbol || ref.name}))

/**
 * Converts backend day codes (e.g. "MON".."SUN","HOL") into DayIdentifier[] in canonical order.
 * Returns `undefined` when the input is absent/empty/unmappable so callers can fall back to DAYS.
 */
export const dayCodesToIdentifiers = (codes?: string[]): DayIdentifier[] | undefined => {
  if (!codes || codes.length === 0) return undefined
  const ids = new Set(
    codes
      .map((code) => DAY_CODE_TO_ID[code.toUpperCase() as DayCode])
      .filter((id): id is DayIdentifier => id !== undefined),
  )
  const ordered = DAYS.filter((day) => ids.has(day))
  return ordered.length > 0 ? ordered : undefined
}

/** Returns the backend list when it has entries, otherwise the hardcoded fallback. */
export const withFallback = <T>(backend: T[] | undefined, fallback: T[]): T[] =>
  backend && backend.length > 0 ? backend : fallback

/**
 * Narrows a backend `string[]` to the known FE union members in `allowed`, dropping any
 * unrecognized codes. Returns `undefined` when the input is absent so callers can fall back.
 * Type-safe narrowing (no blind cast) — unknown backend values never reach label lookups.
 */
export const filterToKnown = <T extends string>(
  values: string[] | undefined,
  allowed: readonly T[],
): T[] | undefined => {
  if (!values) return undefined
  const allowedSet = new Set<string>(allowed)
  return values.filter((v): v is T => allowedSet.has(v))
}
